import * as Sentry from '@sentry/nuxt'

export type SentryFlowCategory =
  | 'withdrawal'
  | 'deposit'
  | 'cashout'
  | 'bet'
  | 'auth'
  | 'websocket'
  | 'game'

/**
 * Sentry helpers for Vue components.
 *
 * For module-level singletons outside Vue context ($fetch.ts, useGameWebSocket.ts),
 * use `import * as Sentry from '@sentry/nuxt'` directly instead.
 */
export function useSentry() {
  function addBreadcrumb(
    category: SentryFlowCategory,
    message: string,
    level: Sentry.SeverityLevel = 'info',
    data?: Record<string, unknown>,
  ): void {
    Sentry.addBreadcrumb({
      category,
      message,
      level,
      data,
    })
  }

  function captureFlowError(
    error: unknown,
    category: SentryFlowCategory,
    context?: Record<string, unknown>,
  ): string {
    return Sentry.captureException(error, {
      tags: { flow: category },
      extra: context,
    })
  }

  function captureFlowMessage(
    message: string,
    category: SentryFlowCategory,
    level: Sentry.SeverityLevel = 'warning',
    context?: Record<string, unknown>,
  ): string {
    return Sentry.captureMessage(message, {
      level,
      tags: { flow: category },
      extra: context,
    })
  }

  function setUser(id: string): void {
    Sentry.setUser({ id })
  }

  function clearUser(): void {
    Sentry.setUser(null)
  }

  return {
    addBreadcrumb,
    captureFlowError,
    captureFlowMessage,
    setUser,
    clearUser,
  }
}
